import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';
import { 
  syncLoginToExtension, 
  syncLogoutToExtension, 
  getExtensionAuthState,
  checkExtensionAuth 
} from '../lib/extension-bridge';

interface User {
  id: string;
  email: string;
  fullName: string | null;
  isSuperAdmin: boolean;
  organizations?: Array<{
    id: string;
    name: string;
    slug: string;
    role: string;
  }>;
  projects?: Array<{
    id: string;
    name: string;
    key: string;
    orgId: string;
    role: string;
  }>;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  extensionConnected: boolean;
  
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName: string, organizationName?: string) => Promise<void>;
  logout: (syncToExtension?: boolean) => void;
  fetchUser: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  setTokenAndFetchUser: (token: string) => Promise<void>;
  syncFromExtension: () => Promise<boolean>;
  checkExtension: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      extensionConnected: false,
      
      login: async (email: string, password: string) => {
        const response = await api.post<{
          token?: string;
          user?: User;
          expiresAt?: string;
          error?: string;
          message?: string;
          needsVerification?: boolean;
        }>('/auth/login', { email, password });
        
        // Check if verification is needed
        if (response.needsVerification) {
          throw new Error(response.message || 'Please verify your email');
        }
        
        if (!response.token) {
          throw new Error(response.message || 'Login failed');
        }
        
        set({
          user: response.user || null,
          token: response.token,
          isAuthenticated: true,
          isLoading: false,
        });
        
        // Fetch full user details
        await get().fetchUser();
        
        // Sync to extension (non-blocking)
        const user = get().user;
        if (response.token && user) {
          syncLoginToExtension(response.token, {
            id: user.id,
            email: user.email,
            fullName: user.fullName || '',
            isSuperAdmin: user.isSuperAdmin,
          }).catch(console.warn);
        }
      },
      
      register: async (email: string, password: string, fullName: string, organizationName?: string) => {
        const response = await api.post<{
          token: string;
          user: User;
          organization: { id: string; name: string; slug: string };
          project: { id: string; name: string; key: string };
          expiresAt: string;
        }>('/auth/register', { email, password, fullName, organizationName });
        
        set({
          user: response.user,
          token: response.token,
          isAuthenticated: true,
          isLoading: false,
        });
        
        // Fetch full user details with org/project memberships
        await get().fetchUser();
        
        // Sync to extension (non-blocking)
        const user = get().user;
        if (response.token && user) {
          syncLoginToExtension(response.token, {
            id: user.id,
            email: user.email,
            fullName: user.fullName || '',
            isSuperAdmin: user.isSuperAdmin,
          }).catch(console.warn);
        }
      },
      
      logout: (syncToExtension = true) => {
        // Sync logout to extension (non-blocking) - skip if logout was initiated by extension
        if (syncToExtension) {
          syncLogoutToExtension().catch(console.warn);
        }
        
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },
      
      fetchUser: async () => {
        const token = get().token;
        if (!token) {
          set({ isLoading: false });
          return;
        }
        
        try {
          const user = await api.get<User>('/auth/me');
          set({ user, isAuthenticated: true, isLoading: false });
        } catch {
          set({ user: null, token: null, isAuthenticated: false, isLoading: false });
        }
      },
      
      setLoading: (loading: boolean) => set({ isLoading: loading }),
      
      setTokenAndFetchUser: async (token: string) => {
        // First set the token in store
        set({ token, isAuthenticated: true, isLoading: true });
        
        // Also force write to localStorage immediately (api.ts reads from there)
        const currentData = localStorage.getItem('ba-auth');
        const parsed = currentData ? JSON.parse(currentData) : { state: {}, version: 0 };
        parsed.state.token = token;
        localStorage.setItem('ba-auth', JSON.stringify(parsed));
        
        // Then fetch user details
        try {
          const user = await api.get<User>('/auth/me');
          set({ user, isAuthenticated: true, isLoading: false });
          // NOTE: Don't sync to extension here - this function is called when receiving
          // tokens from external sources (extension, SSO callbacks). The login/register
          // functions already handle syncing to extension after their own auth flows.
        } catch {
          // If fetch fails, still keep authenticated with token
          set({ isLoading: false });
        }
      },
      
      // Try to sync auth state from extension (auto-login if extension is logged in)
      syncFromExtension: async () => {
        try {
          const extAuth = await getExtensionAuthState();
          
          if (extAuth?.success && extAuth.authState?.isAuthenticated && extAuth.token) {
            console.log('[Auth] Syncing auth from extension:', extAuth.authState.user?.email);
            
            // Set the token and fetch user
            await get().setTokenAndFetchUser(extAuth.token);
            set({ extensionConnected: true });
            return true;
          }
          
          return false;
        } catch (error) {
          console.warn('[Auth] Failed to sync from extension:', error);
          return false;
        }
      },
      
      // Check if extension is installed
      checkExtension: async () => {
        try {
          const result = await checkExtensionAuth();
          const connected = result.installed ?? false;
          set({ extensionConnected: connected });
          return connected;
        } catch {
          set({ extensionConnected: false });
          return false;
        }
      },
    }),
    {
      name: 'ba-auth',
      partialize: (state) => ({ token: state.token }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          state.fetchUser();
        } else {
          // No local token - try to sync from extension
          state?.syncFromExtension().then((synced) => {
            if (!synced) {
              state?.setLoading(false);
            }
          });
        }
      },
    }
  )
);


